/**
 * A local stand-in for the slice of Zod that CMS widgets use to shape their props.
 *
 * WHY THIS EXISTS. 26 public widgets are `"use client"` and did `import { z } from "zod"`
 * to give CMS fields defaults. That put Zod in the landing-page bundle — 453,883 B raw /
 * 102,004 B gzipped in one chunk — and **56% of it was translations**: Zod re-exports a
 * barrel of 52 locale files
 *
 *     // zod/v4/core/index.js:9  AND  zod/v4/classic/external.js:14
 *     export * as locales from "../locales/index.js";
 *
 * and a namespace re-export like that cannot be tree-shaken. Measured with esbuild on a
 * widget-shaped schema: `zod` 310,606 B (45 locale tables), `zod/v4/core` 275,875 B (45),
 * `zod/mini` 290,715 B (45), and only the leaf `zod/v4/classic/schemas.js` came out clean
 * at 55,344 B with 0 — but that path is not in Zod's `exports` map
 * (ERR_PACKAGE_PATH_NOT_EXPORTED) and does not export `coerce`, which the widgets use.
 * So there was no import path out of it.
 *
 * WHY NOT HAND-WRITTEN DEFAULTS. The widgets declare 741 `.optional()` and 490 `.default()`
 * across 27 files but only 29 real constraints (11 `.min`, 18 `.max`). Rewriting 1,300
 * declarations by hand would be an enormous diff, and it would quietly drop the contract
 * that matters: every widget does `if (!parsed.success) return null`, so a malformed CMS
 * payload makes the widget vanish rather than render broken. Keeping the same shape keeps
 * that behaviour identical and makes each widget a one-line import change.
 *
 * SCOPE. This deliberately implements only what the widgets call. It is NOT a Zod clone:
 * no async parsing, no error maps, no i18n, no refinements beyond min/max. If a widget
 * needs something richer, validate it on the server instead of growing this file.
 */

export type Issue = { path: (string | number)[]; message: string };

/**
 * `Schema<T>` is invariant in T — T shows up in `default()`'s parameter, not just in
 * outputs — so `Schema<string>` does NOT satisfy `Schema<unknown>`. Composite factories
 * therefore constrain on this instead, or every `object({...})` infers its shape as `{}`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnySchema = Schema<any>;

/** Mirrors the shape of ZodError closely enough for the `console.error(err)` call sites. */
export class ParseError extends Error {
  readonly issues: Issue[];
  constructor(issues: Issue[]) {
    super(issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`).join("; "));
    this.name = "ParseError";
    this.issues = issues;
  }
}

type Res<T> = { ok: true; value: T } | { ok: false; issues: Issue[] };
const ok = <T,>(value: T): Res<T> => ({ ok: true, value });
const bad = (path: (string | number)[], message: string): Res<never> => ({
  ok: false,
  issues: [{ path, message }],
});

export class Schema<T> {
  /**
   * Phantom field carrying the output type so `infer<typeof S>` can read it. Never
   * assigned at runtime — `declare` keeps it out of the emitted JS entirely.
   */
  declare readonly _out: T;

  constructor(private readonly run: (v: unknown, path: (string | number)[]) => Res<T>) {}

  /** @internal — used by the composite factories below. */
  _run(v: unknown, path: (string | number)[] = []): Res<T> {
    return this.run(v, path);
  }

  safeParse(
    v: unknown
  ):
    | { success: true; data: T; error?: undefined }
    | { success: false; data?: undefined; error: ParseError } {
    const r = this.run(v, []);
    return r.ok
      ? { success: true, data: r.value }
      : { success: false, error: new ParseError(r.issues) };
  }

  parse(v: unknown): T {
    const r = this.run(v, []);
    if (!r.ok) throw new ParseError(r.issues);
    return r.value;
  }

  optional(): Schema<T | undefined> {
    return new Schema<T | undefined>((v, p) =>
      v === undefined ? ok(undefined) : this.run(v, p)
    );
  }

  nullable(): Schema<T | null> {
    return new Schema<T | null>((v, p) => (v === null ? ok(null) : this.run(v, p)));
  }

  nullish(): Schema<T | null | undefined> {
    return new Schema<T | null | undefined>((v, p) =>
      v == null ? ok(v as null | undefined) : this.run(v, p)
    );
  }

  /**
   * Substitutes when the input is absent. Like Zod, the default is returned as-is and is
   * NOT re-validated — and it also covers the case where an inner `.optional()` already
   * resolved to undefined, which is exactly how `.optional().default(x)` chains read.
   */
  default(d: Exclude<T, undefined> | (() => Exclude<T, undefined>)): Schema<Exclude<T, undefined>> {
    const pick = () => (typeof d === "function" ? (d as () => Exclude<T, undefined>)() : d);
    return new Schema((v, p) => {
      if (v === undefined) return ok(pick());
      const r = this.run(v, p);
      if (!r.ok) return r;
      return ok((r.value === undefined ? pick() : r.value) as Exclude<T, undefined>);
    });
  }

  /** Swallows a failure and yields the fallback instead. */
  catch(fallback: T): Schema<T> {
    return new Schema((v, p) => {
      const r = this.run(v, p);
      return r.ok ? r : ok(fallback);
    });
  }

  transform<U>(fn: (v: T) => U): Schema<U> {
    return new Schema((v, p) => {
      const r = this.run(v, p);
      return r.ok ? ok(fn(r.value)) : r;
    });
  }

  /** String length / numeric floor. Applied only when the value is of that kind. */
  min(n: number, message?: string): Schema<T> {
    return new Schema((v, p) => {
      const r = this.run(v, p);
      if (!r.ok) return r;
      const x = r.value as unknown;
      if (typeof x === "string" && x.length < n) return bad(p, message ?? `Must be at least ${n} characters`);
      if (typeof x === "number" && x < n) return bad(p, message ?? `Must be >= ${n}`);
      if (Array.isArray(x) && x.length < n) return bad(p, message ?? `Must have at least ${n} items`);
      return r;
    });
  }

  max(n: number, message?: string): Schema<T> {
    return new Schema((v, p) => {
      const r = this.run(v, p);
      if (!r.ok) return r;
      const x = r.value as unknown;
      if (typeof x === "string" && x.length > n) return bad(p, message ?? `Must be at most ${n} characters`);
      if (typeof x === "number" && x > n) return bad(p, message ?? `Must be <= ${n}`);
      if (Array.isArray(x) && x.length > n) return bad(p, message ?? `Must have at most ${n} items`);
      return r;
    });
  }

  trim(): Schema<T> {
    return new Schema((v, p) => {
      const r = this.run(v, p);
      if (!r.ok) return r;
      return ok((typeof r.value === "string" ? r.value.trim() : r.value) as T);
    });
  }
}

/* ── factories ──────────────────────────────────────────────────────────── */

export const string = (): Schema<string> =>
  new Schema((v, p) => (typeof v === "string" ? ok(v) : bad(p, "Expected string")));

export const number = (): Schema<number> =>
  new Schema((v, p) =>
    typeof v === "number" && !Number.isNaN(v) ? ok(v) : bad(p, "Expected number")
  );

export const boolean = (): Schema<boolean> =>
  new Schema((v, p) => (typeof v === "boolean" ? ok(v) : bad(p, "Expected boolean")));

export const unknown = (): Schema<unknown> => new Schema((v) => ok(v));

/** `z.null()` / `z.undefined()` — used inside unions to widen a field. */
export const nullType = (): Schema<null> =>
  new Schema((v, p) => (v === null ? ok(null) : bad(p, "Expected null")));
export const undefinedType = (): Schema<undefined> =>
  new Schema((v, p) => (v === undefined ? ok(undefined) : bad(p, "Expected undefined")));
export { nullType as null, undefinedType as undefined };

export const literal = <T extends string | number | boolean>(lit: T): Schema<T> =>
  new Schema((v, p) => (v === lit ? ok(lit) : bad(p, `Expected ${String(lit)}`)));

export const enum_ = <const T extends readonly [string, ...string[]]>(
  values: T
): Schema<T[number]> =>
  new Schema((v, p) =>
    typeof v === "string" && (values as readonly string[]).includes(v)
      ? ok(v as T[number])
      : bad(p, `Expected one of ${values.join(" | ")}`)
  );
export { enum_ as enum };

export const array = <S extends AnySchema>(item: S): Schema<S["_out"][]> =>
  new Schema<S["_out"][]>((v, p) => {
    if (!Array.isArray(v)) return bad(p, "Expected array");
    const out: S["_out"][] = [];
    for (let i = 0; i < v.length; i++) {
      const r = item._run(v[i], [...p, i]);
      if (!r.ok) return r;
      out.push(r.value);
    }
    return ok(out);
  });

export const object = <S extends Record<string, AnySchema>>(
  shape: S
): Schema<{ [K in keyof S]: S[K]["_out"] }> =>
  new Schema((v, p) => {
    if (typeof v !== "object" || v === null || Array.isArray(v)) return bad(p, "Expected object");
    const src = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(shape)) {
      const r = shape[key]._run(src[key], [...p, key]);
      if (!r.ok) return r;
      // Match Zod: a key whose value resolves to undefined is omitted, not set to undefined.
      if (r.value !== undefined) out[key] = r.value;
    }
    return ok(out as { [K in keyof S]: S[K]["_out"] });
  });

export const union = <T extends readonly [AnySchema, ...AnySchema[]]>(
  options: T
): Schema<T[number]["_out"]> =>
  new Schema((v, p) => {
    for (const opt of options) {
      const r = opt._run(v, p);
      if (r.ok) return ok(r.value as T[number]["_out"]);
    }
    return bad(p, "No union member matched");
  });

export function record<V extends AnySchema>(value: V): Schema<Record<string, V["_out"]>>;
export function record<K extends AnySchema, V extends AnySchema>(
  key: K,
  value: V
): Schema<Record<string, V["_out"]>>;
/** Keys are always strings once they reach JS, so the key schema is accepted and ignored. */
export function record(a: AnySchema, b?: AnySchema): Schema<Record<string, unknown>> {
  const value = b ?? a;
  return new Schema<Record<string, unknown>>((v, p) => {
    if (typeof v !== "object" || v === null || Array.isArray(v)) return bad(p, "Expected object");
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      const r = value._run(val, [...p, k]);
      if (!r.ok) return r;
      out[k] = r.value;
    }
    return ok(out);
  });
}

/** `z.preprocess(fn, schema)` — reshape the raw value before the schema sees it. */
export const preprocess = <S extends AnySchema>(
  fn: (v: unknown) => unknown,
  schema: S
): Schema<S["_out"]> => new Schema<S["_out"]>((v, p) => schema._run(fn(v), p));

/**
 * `z.coerce.*` — the API the widgets reach for when the CMS/MySQL sends "3" or 0/1.
 * Coercion follows Zod: String(v), Number(v), Boolean(v), with undefined passed through
 * so a following `.optional().default(x)` still sees an absent value rather than "undefined".
 */
export const coerce = {
  string: (): Schema<string> =>
    new Schema((v) => (v === undefined ? ok(undefined as unknown as string) : ok(String(v)))),
  number: (): Schema<number> =>
    new Schema((v, p) => {
      if (v === undefined) return ok(undefined as unknown as number);
      const n = Number(v);
      return Number.isNaN(n) ? bad(p, "Expected number") : ok(n);
    }),
  boolean: (): Schema<boolean> =>
    new Schema((v) => (v === undefined ? ok(undefined as unknown as boolean) : ok(Boolean(v)))),
};

/* ── type helpers (mirror `z.infer` / `z.output` / `ZodTypeAny`) ─────────── */

export type infer<T extends AnySchema> = T["_out"];
export type output<T extends AnySchema> = T["_out"];
export type ZodType<T = unknown> = Schema<T>;
export type ZodTypeAny = AnySchema;
